import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import PoliciesWorkflows from "../../components/admin/settings/PoliciesWorkflows";
import TransactionsPolicyTable, {
  TransactionPolicyActions,
} from "../../components/admin/settings/TransactionsPolicyTable";
import { PageContainer } from "@outpave/ds";
import { useFeatureEntitlements } from "@frontegg/react";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { Inline } from "components/layout/reusableLayouts";
import { usePolicyNavigationGuard } from "hooks/usePolicyNavigationGuard";
import SaveOrDiscardDialog from "components/common/SaveOrDiscardDialog";

const Policies: React.FC = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Policies"));
  const { isEntitled: featureTransactionPolicies } = useFeatureEntitlements(
    "feature_transaction_policies",
  );

  const [activeTab, setActiveTab] = useState(0);

  // The transactions tab owns the save state; the header owns the buttons.
  const [policyActions, setPolicyActions] =
    useState<TransactionPolicyActions | null>(null);
  const handleActionsChange = useCallback(
    (actions: TransactionPolicyActions | null) => setPolicyActions(actions),
    [],
  );

  const tabConfigurations = useMemo(() => {
    const _tabConfigurations = [
      {
        value: "transactions",
        label: t("Transaction Policies"),
        component: (
          <TransactionsPolicyTable onActionsChange={handleActionsChange} />
        ),
        description: t(
          "Customize all requirements users need to submit with transactions ",
        ),
        isInvisible: !featureTransactionPolicies,
      },
      {
        value: "workflows",
        label: t("Workflow Policies"),
        component: <PoliciesWorkflows />,
        description: t(
          "Set the approval process workflow for users requesting new cards or balance increases",
        ),
      },
    ];
    return _tabConfigurations.filter((tab) => !tab.isInvisible);
  }, [featureTransactionPolicies, handleActionsChange, t]);

  // Changing tab unmounts the transactions tab, taking its unsaved category
  // edits with it, so the tab strip has to ask the same question its own role
  // and policy pickers do.
  const tabGuard = usePolicyNavigationGuard<number>({
    isDirty: Boolean(policyActions?.isDirty),
    onApply: setActiveTab,
    onSave: async () => (policyActions ? policyActions.onSave() : true),
    onDiscard: () => policyActions?.onDiscard(),
  });

  useEffect(() => {
    navigate(`/admin/settings/policies/${tabConfigurations[activeTab].value}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <PageContainer
      title={t("Policies")}
      breadcrumbs={breadcrumbs}
      primaryAction={
        policyActions && (
          <Inline gap="var(--space-4)" wrap={false}>
            {policyActions.isDirty ? (
              <>
                <Button
                  variant="primary-outlined"
                  size="medium"
                  onClick={policyActions.onDiscard}
                >
                  {t("settingPolicies.discard")}
                </Button>
                <Button
                  variant="primary-filled"
                  size="medium"
                  onClick={policyActions.onSave}
                >
                  {t("Save")}
                </Button>
              </>
            ) : (
              <Button
                variant="primary-filled"
                size="medium"
                onClick={policyActions.onBulkUpdate}
                disabled={policyActions.bulkUpdateDisabled}
              >
                {t("settingPolicies.bulkUpdate")}
              </Button>
            )}
          </Inline>
        )
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => tabGuard.request(Number(id)),
      }}
    >
      {tabConfigurations[activeTab].component}

      <SaveOrDiscardDialog
        open={tabGuard.isPrompting}
        title={t("settingPolicies.unsavedChangesTitle")}
        message={t("settingPolicies.unsavedChangesDescription")}
        saveLabel={t("Save")}
        discardLabel={t("settingPolicies.discard")}
        onSave={tabGuard.save}
        onDiscard={tabGuard.discard}
        onCancel={tabGuard.cancel}
      />
    </PageContainer>
  );
};

export default Policies;
